'use client';

import { useEffect, useRef, useState } from 'react';
import { roundRectPath, wrapText } from '@/components/tools/canvas-mockup';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

type Platform = 'ios' | 'android';

const platforms: Record<
  Platform,
  {
    label: string;
    card: string;
    cardText: string;
    meta: string;
    radius: number;
    // iOS puts the app name in small caps above the title; Android puts it inline with the time.
    appNameCase: 'upper' | 'normal';
  }
> = {
  ios: {
    label: 'iPhone',
    card: '#f2f2f7',
    cardText: '#000000',
    meta: '#8e8e93',
    radius: 22,
    appNameCase: 'upper',
  },
  android: {
    label: 'Android',
    card: '#ffffff',
    cardText: '#1f1f1f',
    meta: '#5f6368',
    radius: 28,
    appNameCase: 'normal',
  },
};

export default function BrowserNotificationGenerator() {
  const [platform, setPlatform] = useState<Platform>('ios');
  const [appName, setAppName] = useState('Messages');
  const [title, setTitle] = useState('Alex');
  const [body, setBody] = useState('Are we still on for tonight?');
  const [time, setTime] = useState('now');
  const [accent, setAccent] = useState('#34c759');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const p = platforms[platform];
    const scale = 2;
    const width = 440;
    const outerPad = 16;
    const cardPad = 14;
    const icon = 38;
    const cardWidth = width - outerPad * 2;
    const textX = outerPad + cardPad + icon + 12;
    const textWidth = cardWidth - cardPad * 2 - icon - 12;
    const lineHeight = 21;

    ctx.font = '400 16px system-ui, sans-serif';
    const bodyLines = wrapText(ctx, body, textWidth);
    const cardHeight = Math.max(
      icon + cardPad * 2,
      cardPad * 2 + 22 + bodyLines.length * lineHeight,
    );
    const totalHeight = cardHeight + outerPad * 2;

    canvas.width = width * scale;
    canvas.height = totalHeight * scale;
    ctx.scale(scale, scale);

    // A soft neutral behind the card so the rounded corners read on any background.
    ctx.fillStyle = '#d9dde4';
    ctx.fillRect(0, 0, width, totalHeight);

    ctx.fillStyle = p.card;
    roundRectPath(ctx, outerPad, outerPad, cardWidth, cardHeight, p.radius);
    ctx.fill();

    // App icon: a rounded square filled with the accent colour, the way a real icon reads at this size.
    ctx.fillStyle = accent;
    roundRectPath(
      ctx,
      outerPad + cardPad,
      outerPad + cardPad,
      icon,
      icon,
      p.appNameCase === 'upper' ? 9 : 19,
    );
    ctx.fill();

    let y = outerPad + cardPad + 13;
    ctx.fillStyle = p.meta;
    ctx.font = '600 12px system-ui, sans-serif';
    const name = p.appNameCase === 'upper' ? appName.toUpperCase() : appName;
    ctx.fillText(name, textX, y);

    if (time.trim()) {
      const t = time.trim();
      ctx.textAlign = 'right';
      ctx.fillText(t, outerPad + cardWidth - cardPad, y);
      ctx.textAlign = 'left';
    }

    y += 19;
    ctx.fillStyle = p.cardText;
    ctx.font = '600 16px system-ui, sans-serif';
    if (title.trim()) {
      ctx.fillText(title.trim(), textX, y);
      y += lineHeight;
    }

    ctx.font = '400 16px system-ui, sans-serif';
    for (const line of bodyLines) {
      ctx.fillText(line, textX, y);
      y += lineHeight;
    }

    canvas.toBlob((blob) => {
      if (blob) setDownloadUrl(URL.createObjectURL(blob));
    }, 'image/png');
  }, [platform, appName, title, body, time, accent]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Mockup</span>
        <h2 className="tool-live-title">Fake notification generator</h2>
        <p className="tool-live-subtitle">
          Build an iPhone or Android notification banner and download it as an image. Rendered
          entirely in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field">
            <span>Platform</span>
            <select onChange={(e) => setPlatform(e.target.value as Platform)} value={platform}>
              <option value="ios">iPhone</option>
              <option value="android">Android</option>
            </select>
          </label>
          <label className="tool-live-field">
            <span>App name</span>
            <input onChange={(e) => setAppName(e.target.value)} type="text" value={appName} />
          </label>
          <label className="tool-live-field">
            <span>Title</span>
            <input onChange={(e) => setTitle(e.target.value)} type="text" value={title} />
          </label>
          <label className="tool-live-field tool-live-field-full">
            <span>Message</span>
            <textarea onChange={(e) => setBody(e.target.value)} rows={3} value={body} />
          </label>
          <label className="tool-live-field">
            <span>Time</span>
            <input onChange={(e) => setTime(e.target.value)} type="text" value={time} />
          </label>
          <label className="tool-live-field">
            <span>Icon colour</span>
            <input onChange={(e) => setAccent(e.target.value)} type="color" value={accent} />
          </label>
          <div className="tool-live-actions">
            {downloadUrl ? (
              <a
                className="button button-secondary"
                download="fake-notification.png"
                href={downloadUrl}
                // Fire-and-forget analytics only; see the same note in BrowserChatGenerator.tsx.
                onClick={() => {
                  void consumeToolUsage('fake-notification-generator', '').catch(() => {});
                }}
              >
                Download PNG
              </a>
            ) : null}
          </div>
        </section>

        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              border: '1px solid rgba(0,0,0,0.1)',
            }}
          />
          <p className="tool-live-panel-note">
            Rendered locally with an HTML canvas. This is a mockup for design and fun.
          </p>
        </section>
      </div>
    </div>
  );
}
